import { createContext, useCallback, useContext, useMemo, useState } from "react";

type SheetId = "poi" | "navigation" | null;

interface SheetOverlayState {
  activeSheet: SheetId;
  snapPoint: number | string | null;
  /** Overlay opacity: 0, 0.2, or 0.4 based on active sheet + snap */
  overlayOpacity: number;
  setSheet: (id: SheetId, snap: number | string | null) => void;
  clearSheet: () => void;
}

const SheetOverlayContext = createContext<SheetOverlayState | null>(null);

/** Derive overlay opacity from Vaul snap point values */
function deriveOpacity(
  sheet: SheetId,
  snap: number | string | null,
): number {
  if (!sheet) return 0;
  // Vaul snap values: "160px" (peek), 0.55 (half), 0.92 (full)
  if (typeof snap === "string") return 0.2; // peek (pixel string)
  if (typeof snap === "number" && snap > 0) return 0.4; // half or full
  return 0;
}

export function SheetOverlayProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [activeSheet, setActiveSheet] = useState<SheetId>(null);
  const [snapPoint, setSnapPoint] = useState<number | string | null>(null);

  const setSheet = useCallback(
    (id: SheetId, snap: number | string | null) => {
      setActiveSheet(id);
      setSnapPoint(snap);
    },
    [],
  );

  const clearSheet = useCallback(() => {
    setActiveSheet(null);
    setSnapPoint(null);
  }, []);

  const overlayOpacity = deriveOpacity(activeSheet, snapPoint);

  const value = useMemo(
    () => ({ activeSheet, snapPoint, overlayOpacity, setSheet, clearSheet }),
    [activeSheet, snapPoint, overlayOpacity, setSheet, clearSheet],
  );

  return (
    <SheetOverlayContext.Provider value={value}>
      {children}
    </SheetOverlayContext.Provider>
  );
}

export function useSheetOverlay(): SheetOverlayState {
  const ctx = useContext(SheetOverlayContext);
  if (!ctx) {
    throw new Error("useSheetOverlay must be used within SheetOverlayProvider");
  }
  return ctx;
}
